import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowUpRight,
  Briefcase,
  ExternalLink,
  Link2,
  Linkedin,
  Mail,
  MoreVertical,
  Phone,
  UserCheck,
} from "lucide-react";
import { useCallback, useState } from "react";
import { Link, useRoute } from "wouter";
import { ApplicantDetailModal } from "@/components/applicant-detail-modal";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useAuth } from "@/hooks/use-auth";
import { useQueryParams } from "@/hooks/use-query-params";
import { useToast } from "@/hooks/use-toast";
import { formatDate } from "@/lib/constants";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";

export default function ApplicantDetail() {
  const [, params] = useRoute("/applicants/:id");
  const applicantId = params?.id;
  const { isSuperAdmin } = useAuth();
  const { toast } = useToast();
  const { getParam, setParam, removeParam } = useQueryParams();
  const selectedApplicationId = getParam("applicationId");
  const isDetailModalOpen = !!selectedApplicationId;

  const setSelectedApplicationId = useCallback(
    (id: string | null) => {
      if (id) {
        setParam("applicationId", id);
      } else {
        removeParam("applicationId");
      }
    },
    [setParam, removeParam],
  );

  const setIsDetailModalOpen = useCallback(
    (open: boolean) => {
      if (!open) {
        removeParam("applicationId");
      }
    },
    [removeParam],
  );

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");

  const applicantQuery = orpc.applicants.getById.queryOptions({
    input: { id: applicantId! },
  });
  const { data: applicant, isLoading } = useQuery({
    ...applicantQuery,
    enabled: !!applicantId,
  });

  const { data: employees } = useQuery({
    ...orpc.employees.list.queryOptions(),
    enabled: linkDialogOpen,
  });

  const linkMutation = useMutation({
    mutationFn: async (employeeId: string | null) => {
      return rpcClient.applicants.linkEmployee({ id: applicantId!, employeeId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: applicantQuery.queryKey });
      toast({ title: "Colaborador associado com sucesso" });
      setLinkDialogOpen(false);
      setSelectedEmployeeId("");
    },
    onError: () => {
      toast({
        title: "Erro ao associar colaborador",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-2 p-6">
        {["a", "b", "c", "d", "e"].map((k) => (
          <div key={k} className="flex items-center gap-3 py-2">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--sand-divider))]" />
            <span className="h-4 flex-1 max-w-md rounded bg-[hsl(var(--sand-divider))]" />
          </div>
        ))}
      </div>
    );
  }

  if (!applicant) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Candidato não encontrado</p>
      </div>
    );
  }

  const initials = applicant.fullName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="flex-1 space-y-6">
      <PageHeader
        title={applicant.fullName}
        backTo={isSuperAdmin ? "/applicants" : undefined}
        onBack={!isSuperAdmin ? () => window.history.back() : undefined}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" data-testid="button-applicant-actions">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {applicant.employeeId ? (
              <DropdownMenuItem asChild>
                <Link href={`/employees/${applicant.employeeId}`}>
                  <UserCheck className="h-4 w-4 mr-2" />
                  Ver colaborador
                </Link>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => setLinkDialogOpen(true)}>
                <Link2 className="h-4 w-4 mr-2" />
                Associar a colaborador
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </PageHeader>

      {/* Applicant info card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <Avatar className="h-16 w-16">
              <AvatarFallback className="bg-[hsl(var(--coral-mist))] text-[hsl(var(--comp-coral))] text-lg font-medium">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-semibold">{applicant.fullName}</h2>
                {applicant.employeeId && (
                  <Link href={`/employees/${applicant.employeeId}`}>
                    <span className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                      <UserCheck className="h-3 w-3" />
                      Colaborador
                    </span>
                  </Link>
                )}
              </div>
              <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                {applicant.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="h-3.5 w-3.5" />
                    {applicant.email}
                  </span>
                )}
                {applicant.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" />
                    {applicant.phone}
                  </span>
                )}
                {applicant.linkedinUrl && (
                  <a
                    href={applicant.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 hover:text-primary transition-colors"
                  >
                    <Linkedin className="h-3.5 w-3.5" />
                    LinkedIn
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Applications list */}
      <div className="space-y-3">
        <h3 className="text-[11px] uppercase font-semibold text-[hsl(var(--ink-muted))] tracking-[0.02em]">
          Candidaturas ({applicant.applications.length})
        </h3>
        {applicant.applications.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-[hsl(var(--ink-muted))]">
              <Briefcase className="w-10 h-10 mb-3 opacity-50" />
              <p>Nenhuma candidatura. Adicione a primeira candidatura.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {applicant.applications.map((app) => (
              <Card
                key={app.id}
                className="cursor-pointer transition-shadow hover:shadow-md"
                onClick={() => setSelectedApplicationId(app.id)}
                data-testid={`card-application-${app.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <Briefcase className="h-4 w-4 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{app.jobTitle || "Sem vaga"}</p>
                        <p className="text-xs text-muted-foreground">
                          {app.jobDepartment || "-"} &middot;{" "}
                          {app.appliedAt ? formatDate(app.appliedAt) : "-"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <StatusBadge status={app.status} />
                      <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Link to employee dialog */}
      <Dialog
        open={linkDialogOpen}
        onOpenChange={(open) => {
          setLinkDialogOpen(open);
          if (!open) setSelectedEmployeeId("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Associar a colaborador</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <SearchableSelect
              options={(employees || []).map((emp) => ({
                value: emp.id,
                label: `${emp.fullName}${emp.corporateEmail ? ` (${emp.corporateEmail})` : ""}`,
              }))}
              value={selectedEmployeeId}
              onValueChange={setSelectedEmployeeId}
              placeholder="Buscar colaborador..."
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={() => linkMutation.mutate(selectedEmployeeId)}
              disabled={!selectedEmployeeId || linkMutation.isPending}
            >
              {linkMutation.isPending ? "Associando..." : "Associar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Application detail modal */}
      <ApplicantDetailModal
        applicationId={selectedApplicationId}
        open={isDetailModalOpen}
        onOpenChange={setIsDetailModalOpen}
        propertyDefinitions={[]}
        propertyValues={[]}
      />
    </div>
  );
}
